import { useMemo } from 'react'
import {
    TrafficSourceOptionType,
    UsersSortOptionType,
    SubscriberStatusSortOptionType,
    LineDateSortOptionType,
} from '../../constants/analyticsSelectors'
import { AudienceTableRowType } from '@/features/analytics/model/slices/audienceSlice'

interface UseSelectProps {
    data: AudienceTableRowType[]
    startDate: string
    endDate: string
    lineDate: LineDateSortOptionType | null
    trafficSource: TrafficSourceOptionType
    usersSort: UsersSortOptionType
    subscriberStatus: SubscriberStatusSortOptionType
    channelCreatedAt: Date | undefined
}

export const useSelect = ({
    data,
    startDate,
    endDate,
    lineDate,
    trafficSource,
    usersSort,
    subscriberStatus,
    channelCreatedAt,
}: UseSelectProps) => {
    const selectedData = useMemo(() => {
        return data
            .filter((item) => {
                const itemDate = new Date(item.subscriptionDate ? item.subscriptionDate : channelCreatedAt ? channelCreatedAt : new Date().toDateString()).setHours(
                    0,
                    0,
                    0,
                    0
                )
                const startDateTime = new Date(startDate).setHours(0, 0, 0, 0)
                const endDateTime = new Date(endDate).setHours(23, 59, 59, 1000)
                return itemDate >= startDateTime && itemDate <= endDateTime
            })
            .filter((item) => {
                if (trafficSource.value === 'allTraffic') return true
                if (trafficSource.value === 'inviteLinks') return item.source
                if (trafficSource.value === 'anySource') return !item.source
                return true
            })
            .filter((item) => {
                if (usersSort.value === 'allUsers') return true
                if (usersSort.value === 'premiumUsers') return item.isPremium
                if (usersSort.value === 'noPremium') return !item.isPremium
                return true
            })
            .filter((item) => {
                if (subscriberStatus.value === 'all') return true
                if (subscriberStatus.value === 'subscribed')
                    return !item.unsubscriptionDate
                if (subscriberStatus.value === 'unsubscribed')
                    return !!item.unsubscriptionDate
                return true
            })
            .filter((item) => {
                const itemDate = new Date(item.subscriptionDate ? item.subscriptionDate : channelCreatedAt ? channelCreatedAt : new Date().toDateString())
                const currentDate = new Date()
                if (!lineDate) return true
                switch (lineDate.value) {
                    case 'today':
                        return (
                            itemDate.toDateString() ===
                            currentDate.toDateString()
                        )
                    case 'yesterday':
                        const yesterday = new Date()
                        yesterday.setDate(currentDate.getDate() - 1)
                        return (
                            itemDate.toDateString() === yesterday.toDateString()
                        )
                    case 'week':
                        const weekAgo = new Date()
                        weekAgo.setDate(currentDate.getDate() - 7)
                        return itemDate >= weekAgo
                    case 'month':
                        const monthAgo = new Date()
                        monthAgo.setMonth(currentDate.getMonth() - 1)
                        return itemDate >= monthAgo
                    case 'quarter':
                        const quarterAgo = new Date()
                        quarterAgo.setMonth(currentDate.getMonth() - 3)
                        return itemDate >= quarterAgo
                    case 'year':
                        const yearAgo = new Date()
                        yearAgo.setFullYear(currentDate.getFullYear() - 1)
                        return itemDate >= yearAgo
                    default:
                        return true
                }
            })
    }, [
        data,
        startDate,
        endDate,
        trafficSource,
        usersSort,
        subscriberStatus,
        lineDate,
    ])

    return { selectedData }
}

